import { paths } from "../paths";
import { Pose } from "../pose";

const ONES = new THREE.Vector3(1, 1, 1);
const m = new THREE.Matrix4();
const GRAB_DELAY_FRAMES = 2; // Adjust this value based on testing (start small)

export class WebXRMobileControllerDevice {
  constructor(gamepad) {
    this.gamepad = gamepad; // Might become null if disconnected

    this.hand = gamepad.hand === "left" ? "left" : "right";
    this.selector = `#player-${this.hand}-controller`;
    this.rayObject = null;
    this.rayObjectRotation = new THREE.Quaternion();
    this.pose = new Pose();

    this.matrix = new THREE.Matrix4();
    this.position = new THREE.Vector3();
    this.orientation = new THREE.Quaternion();

    // --- State for Grab Delay ---
    this.isTriggerDown = false; // Tracks the raw button state
    this.framesUntilGrab = -1; // Counter for the delay
    this.delayedTriggerPressed = false; // The delayed signal for bindings
    this.framesUntilUnassign = -1; // Counter for ensuring drop signal on quick taps
    // --- End State ---
  }

  write(frame, xrFrame, referenceSpace) {
    if (!referenceSpace || !xrFrame) return;

    const hand = this.hand;
    const path = paths.device.webxr[hand];

    // --- Pose Update (Do this early) ---
    this.rayObject = this.rayObject || document.querySelector(this.selector)?.object3D; // Added safety check
    if (this.rayObject) {
      this.rayObject.updateMatrixWorld();
      this.rayObjectRotation.setFromRotationMatrix(m.extractRotation(this.rayObject.matrixWorld));
      this.pose.position.setFromMatrixPosition(this.rayObject.matrixWorld);
      this.pose.direction.set(0, 0, -1).applyQuaternion(this.rayObjectRotation);
      this.pose.orientation.copy(this.rayObjectRotation);
      frame.setPose(path.pose, this.pose);

      // Update matrix from targetRaySpace if gamepad available
      if (this.gamepad) {
        const pose = xrFrame.getPose(this.gamepad.targetRaySpace, referenceSpace);
        if (pose?.transform?.position && pose?.transform?.orientation) {
          this.position.copy(pose.transform.position);
          this.orientation.copy(pose.transform.orientation);
          this.matrix.compose(this.position, this.orientation, ONES);
          frame.setMatrix4(path.matrix, this.matrix);
        }
      }
    } else {
      // Handle case where rayObject isn't found? Maybe clear pose/matrix?
      // console.warn(`Ray object not found for selector: ${this.selector}`);
    }
    // --- End Pose Update ---

    // --- Button Input and Delay Logic ---
    let currentTriggerState = false;
    let currentTriggerValue = 0;

    if (this.gamepad && this.gamepad.buttons[2]) {
      // Use .pressed if available and reliable, otherwise fallback to .touched
      // Experiment here to see which gives the best raw signal for your device
      currentTriggerState = this.gamepad.buttons[2].pressed || this.gamepad.buttons[2].touched;
      currentTriggerValue = this.gamepad.buttons[2].value;
    }

    const triggerJustPressed = currentTriggerState && !this.isTriggerDown;
    const triggerJustReleased = !currentTriggerState && this.isTriggerDown;

    if (triggerJustPressed) {
      // Start the delay counter ONLY if not already counting down or active
      if (this.framesUntilGrab < 0 && !this.delayedTriggerPressed) {
        this.framesUntilGrab = GRAB_DELAY_FRAMES;
      }
      // Reset unassign counter if active
      this.framesUntilUnassign = -1;
    }

    if (this.framesUntilGrab > 0) {
      this.framesUntilGrab--;
    }

    // Activate the delayed signal when the counter hits zero
    if (this.framesUntilGrab === 0) {
      this.delayedTriggerPressed = true;
      this.framesUntilGrab = -1; // Stop counting
    }

    if (triggerJustReleased) {
      if (this.framesUntilGrab >= 0) {
        // Released *during* the grab delay (quick tap)
        // Activate grab briefly and schedule unassign
        this.delayedTriggerPressed = true;
        this.framesUntilGrab = -1;
        this.framesUntilUnassign = 2; // Keep pressed state for 2 more frames
      } else if (this.delayedTriggerPressed) {
        // Released *after* grab was active
        // Deactivate immediately unless unassign is scheduled
        if (this.framesUntilUnassign < 0) {
          this.delayedTriggerPressed = false;
        }
      }
      // Ensure counter is stopped if release happens
      this.framesUntilGrab = -1;
    }

    // Handle scheduled unassignment
    if (this.framesUntilUnassign > 0) {
      this.framesUntilUnassign--;
    }
    if (this.framesUntilUnassign === 0) {
      this.delayedTriggerPressed = false;
      this.framesUntilUnassign = -1;
    }

    // Update the raw state tracker
    this.isTriggerDown = currentTriggerState;

    // --- Write Delayed Values to Frame ---
    // IMPORTANT: Use the delayed state for bindings needing hold behavior
    frame.setValueType(path.button.trigger.pressed, this.delayedTriggerPressed);
    // You might want value to reflect the delayed state too (0 or original value)
    frame.setValueType(path.button.trigger.value, this.delayedTriggerPressed ? currentTriggerValue : 0);

    // Optional: Write raw values if needed elsewhere (use different paths)
    // frame.setValueType(path.button.trigger.rawPressed, currentTriggerState);
    // frame.setValueType(path.button.trigger.rawValue, currentTriggerValue);
    // --- End Button Input and Delay Logic ---
  }
}
